// ─────────────────────────────────────────────
// KLOTHIQ — BrandLoader
// FEAT-LOADER-001: shared "Teal Pulse Reveal" loading indicator.
// Two weights of the same visual language:
//   variant="boot"   — full-screen, hero-sized, shows the logo in a
//                       .glass-card (see spec FR-002/UNK-001 for why a
//                       card, not a floating transparent mark)
//   variant="inline"  — compact, no logo (spec FR-006), used wherever
//                       LoadingState was used before
// ─────────────────────────────────────────────

import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { useDeferredVisible } from '@/hooks/useDeferredVisible';

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  useEffect(() => {
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReduced(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  return reduced;
}

function Ring({ size, reducedMotion }: { size: number; reducedMotion: boolean }) {
  return (
    <div
      className={cn('absolute inset-0 rounded-full', reducedMotion ? 'loader-ring loader-ring-pulse' : 'loader-ring loader-ring-spin')}
      style={{ width: size, height: size }}
      aria-hidden="true"
    />
  );
}

// Second, larger, fainter, counter-rotating ring layered behind the main
// one — purely a depth/richness detail, sized bigger than the wrapper so
// it visually extends past it (no clipping — nothing here sets overflow).
function OuterRing({ size, reducedMotion }: { size: number; reducedMotion: boolean }) {
  const outerSize = Math.round(size * 1.32);
  return (
    <div
      className={cn(
        'absolute rounded-full',
        reducedMotion ? 'loader-ring-outer loader-ring-outer-pulse' : 'loader-ring-outer loader-ring-outer-spin'
      )}
      style={{
        width: outerSize,
        height: outerSize,
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      }}
      aria-hidden="true"
    />
  );
}

function Glow({ reducedMotion }: { reducedMotion: boolean }) {
  return (
    <div
      className={cn('pointer-events-none absolute inset-[-40%] rounded-full', reducedMotion ? 'loader-glow-static' : 'loader-glow')}
      aria-hidden="true"
    />
  );
}

export interface BrandLoaderProps {
  /** "boot" = full-screen hero loader with logo card. "inline" = compact, no logo. */
  variant?: 'boot' | 'inline';
  /** Gate that drives visibility — e.g. AuthContext.isLoading, or a query's isLoading. */
  active?: boolean;
  message?: string;
  className?: string;
  /** Skip the 200ms appearance-delay / 400ms minimum-duration debounce — used for the boot
   *  variant where the caller (router.tsx) already only renders this while active is true. */
  immediate?: boolean;
  /** Translucent fixed backdrop over existing content instead of an opaque
   *  full h-screen background — used by MutationLoadingOverlay, where
   *  there's real page content underneath that should stay dimly visible. */
  overlay?: boolean;
}

export default function BrandLoader({
  variant = 'inline',
  active = true,
  message = 'Loading...',
  className,
  immediate = false,
  overlay = false,
}: BrandLoaderProps) {
  const reducedMotion = usePrefersReducedMotion();
  const deferredVisible = useDeferredVisible(active);
  const visible = immediate ? active : deferredVisible;

  if (!visible) return null;

  const size = variant === 'boot' ? 200 : 48;
  const cardSize = variant === 'boot' ? 132 : 0;

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'flex flex-col items-center justify-center',
        overlay
          ? 'fixed inset-0 z-50 bg-background/70 backdrop-blur-sm'
          : variant === 'boot'
            ? 'h-screen w-full bg-background'
            : 'py-14',
        className
      )}
    >
      <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
        <Glow reducedMotion={reducedMotion} />
        <OuterRing size={size} reducedMotion={reducedMotion} />
        <Ring size={size} reducedMotion={reducedMotion} />
        {variant === 'boot' && (
          <div
            className={cn('glass-card relative flex items-center justify-center overflow-hidden', reducedMotion ? 'loader-logo-enter-reduced' : 'loader-logo-enter')}
            style={{ width: cardSize, height: cardSize }}
          >
            <img
              src="/logo.png"
              alt="Klothiq"
              className="h-[78%] w-[78%] object-contain"
              draggable={false}
            />
          </div>
        )}
      </div>
      <p className="mt-4 text-sm text-muted-foreground">{message}</p>
    </div>
  );
}
